import React, { useEffect, useRef, useState } from "react";
import { Card, Button, Input, Space, Typography, Tag } from "antd";
import {
  SearchOutlined,
  LogoutOutlined,
  TeamOutlined,
  TableOutlined
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import { customBaseUrl, httpGet } from "../../store/http";
import { hideLoader, showLoader } from "../../utils/loader";
import { NotificationManager } from "react-notifications";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import LoanSubmission from "./submission";
import { useMatStyles } from "./style";
import noDataLogo from "../../assets/images/no-data.png";

const { Title, Text } = Typography;

const LoanOverview = () => {
  const navigate = useNavigate();
  const classes = useMatStyles();

  const [request, setRequest] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSubmission, setShowSubmission] = useState(false);
  const [selectedRecord, setRecord] = useState(null);
  const [showProfiles, setShowProfile] = useState(false);
  const [meta, setMeta] = useState(0);
  const [pagination, setPagination] = useState({
    current: 0,
    pageSize: 20,
  });

  const getLaonRequest = async (page = 0, size = 20) => {
    showLoader();
    setLoading(true);
    const approverRole = localStorage.getItem("approverRole");
    const status = approverRole === 'Credit analyst' ? 'Pending'
      : approverRole === 'Controller' ? 'First Level Approved'
        : 'Second Level Approved';

    try {
      const res = await httpGet(
        `${customBaseUrl.loanUrl}/api/v1/approver/loan/by-status?page=${page}&size=${size}&status=${status}`
      );
      if (res.status) {
        setRequest(res.data.loans);
        setMeta(res.data.totalItems);
      }
    } catch (error) {
      NotificationManager.error("Failed to fetch loan requests");
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  useEffect(() => {
    getLaonRequest();
  }, []);

  const logout = () => {
    localStorage.clear();
    window.location.href = '/auth/loan/login';
  };

  const columns = [
    {
      field: 'applicant',
      headerName: 'Applicant',
      flex: 1,
      minWidth: 250,
      headerClassName: classes.tableHeader,
      renderCell: ({ row }) => (
        <Space direction="vertical" size={0}>
          <Text strong>{row?.firstName} {row?.lastName}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{row?.emailAddress}</Text>
        </Space>
      )
    },
    {
      field: 'phoneNumber',
      headerName: 'Contact',
      flex: 0.8,
      minWidth: 150,
      headerClassName: classes.tableHeader,
    },
    {
      field: 'typeOfLoan',
      headerName: 'Loan Type',
      flex: 0.8,
      minWidth: 150,
      headerClassName: classes.tableHeader,
      renderCell: ({ row }) => (
        <Tag color="orange" style={{ borderRadius: 6, fontWeight: 600 }}>{row?.typeOfLoan}</Tag>
      )
    },
    {
      field: 'actions',
      headerName: 'Action',
      width: 150,
      sortable: false,
      headerClassName: classes.tableHeader,
      renderCell: ({ row }) => (
        <Button
          type="primary"
          size="small"
          style={{ background: '#FF4B01', border: 'none', borderRadius: 6 }}
          onClick={() => {
            setRecord(row);
            setShowSubmission(true);
          }}
        >
          View Details
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: '32px', background: '#f8f9fa', minHeight: '100vh' }}>
      {showSubmission ? (
        <LoanSubmission
          data={selectedRecord}
          show={showSubmission}
          hide={setShowSubmission}
          getReq={getLaonRequest}
        />
      ) : (
        <>
          {/* Header Section */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 }}>
            <div>
              <Title level={2} style={{ margin: 0, fontWeight: 800 }}>Loan Requests</Title>
              <Text type="secondary">Manage and review all incoming loan applications</Text>
            </div>
            <Space>
              <Button
                icon={<LogoutOutlined />}
                onClick={logout}
                style={{ borderRadius: 8 }}
              >
                Logout
              </Button>
            </Space>
          </div>

          {/* Navigation Tabs */}
          <div style={{
            background: '#fff',
            padding: '4px',
            borderRadius: 12,
            display: 'inline-flex',
            marginBottom: 24,
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            border: '1px solid #eee'
          }}>
            <Button
              type={!showProfiles ? "primary" : "text"}
              icon={<TableOutlined />}
              style={{
                borderRadius: 8,
                height: 40,
                background: !showProfiles ? '#FF4B01' : 'transparent',
                fontWeight: !showProfiles ? 600 : 400
              }}
              onClick={() => setShowProfile(false)}
            >
              Overview
            </Button>
            <Button
              type={showProfiles ? "primary" : "text"}
              icon={<TeamOutlined />}
              style={{
                borderRadius: 8,
                height: 40,
                background: showProfiles ? '#FF4B01' : 'transparent',
                fontWeight: showProfiles ? 600 : 400
              }}
              onClick={() => setShowProfile(true)}
            >
              Approvers
            </Button>
          </div>

          <Card style={{ borderRadius: 16, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
            {request?.length ? (
              <div style={{ height: 600, width: "100%" }}>
                <DataGrid
                  className={classes.table}
                  rows={request}
                  columns={columns}
                  pageSize={pagination.pageSize}
                  onPageSizeChange={(newPageSize) => setPagination(prev => ({ ...prev, pageSize: newPageSize }))}
                  onPageChange={(newPage) => getLaonRequest(newPage, pagination.pageSize)}
                  rowCount={meta}
                  pagination
                  paginationMode="server"
                  components={{
                    Toolbar: () => (
                      <div style={{ padding: '16px', borderBottom: '1px solid #f0f0f0' }}>
                        <GridToolbar />
                      </div>
                    )
                  }}
                  disableSelectionOnClick
                />
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <img src={noDataLogo} alt="nodata" style={{ height: "200px", marginBottom: 24 }} />
                <Title level={4}>No Requests Found</Title>
                <Text type="secondary">New loan applications will appear here for your review.</Text>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
};

export default LoanOverview;
